import React, { useState, useEffect, useRef } from "react";
import {
  Card, CardHeader, CardBody, CardTitle,
  Row, Col, Button, Input, FormGroup, Label, Spinner,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import "react-notification-alert/dist/animate.css";
import axiosInstance from "service/axios";
import { CicloService } from "service/CicloService";
import { SalonService } from "service/SalonService";
import FSelect from "components/Select/FSelect";

const cicloService = new CicloService();
const salonService = new SalonService();

function CrearCarnet() {
  const notificationAlertRef = useRef(null);
  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tc",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const [listCiclos, setListCiclos] = useState([]);
  const [listSalones, setListSalones] = useState([]);
  const [dni, setDni] = useState("");
  const [alumno, setAlumno] = useState(null);
  const [cicloSeleccionado, setCicloSeleccionado] = useState("");
  const [salonSeleccionado, setSalonSeleccionado] = useState("");
  const [loadingBuscar, setLoadingBuscar] = useState(false);
  const [loadingCarnet, setLoadingCarnet] = useState(false);
  const [mensaje, setMensaje] = useState("");

  useEffect(() => {
    getCiclos();
    getSalones();
  }, []);

  const getCiclos = async () => {
    try {
      const data = await cicloService.getCiclos();
      const items = Array.isArray(data) ? data : data?.data || [];
      setListCiclos(items);
      if (items.length > 0) setCicloSeleccionado(items[0].tipociclo || items[0]._id);
    } catch (error) {
      console.error("Error cargando ciclos:", error);
    }
  };

  const getSalones = async () => {
    try {
      const data = await salonService.getSalones();
      const items = Array.isArray(data) ? data : data?.data || [];
      setListSalones(items);
      if (items.length > 0) setSalonSeleccionado(items[0].denominacion || items[0]._id);
    } catch (error) {
      console.error("Error cargando salones:", error);
    }
  };

  const buscarAlumno = async () => {
    if (!dni || dni.length < 8) {
      notify("warning", "Ingresa un DNI de 8 dígitos");
      return;
    }
    setLoadingBuscar(true);
    setAlumno(null);
    setMensaje("");
    try {
      const response = await axiosInstance.get("/alumno", {
        params: { documentoidentidad: dni },
      });
      const data = Array.isArray(response.data) ? response.data[0] : response.data?.data?.[0] || response.data;
      if (data?._id) {
        setAlumno(data);
      } else {
        setMensaje("No se encontró alumno con ese DNI.");
      }
    } catch (error) {
      console.error("Error buscando alumno:", error);
      setMensaje("Error al buscar el alumno.");
    } finally {
      setLoadingBuscar(false);
    }
  };

  const generarCarnet = () => {
    if (!alumno) { notify("warning", "Primero busca un alumno"); return; }
    if (!cicloSeleccionado || !salonSeleccionado) { notify("warning", "Selecciona ciclo y salón"); return; }

    const datos = [
      alumno.documentoidentidad,
      (alumno.nombres || "").toUpperCase(),
      (alumno.apellidopaterno || "").toUpperCase(),
      (alumno.apellidomaterno || "").toUpperCase(),
      cicloSeleccionado.toUpperCase(),
      salonSeleccionado.toUpperCase(),
      "CENTRAL",
      alumno.urlimagen || "",
    ].join("|");

    const url = `${process.env.REACT_APP_ROUTE_API}/reporte/carnet/${encodeURIComponent(datos)}`;
    window.open(url, "_blank");
  };

  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />
      <div className="content">
        <Row>
          <Col md="8" className="mx-auto">
            <Card>
              {/* Zona 1: Header */}
              <CardHeader>
                <Row className="align-items-center">
                  <Col md="8">
                    <CardTitle tag="h4">Crear Carnet de Alumno</CardTitle>
                  </Col>
                  <Col md="4" className="text-right">
                    <Button color="link" className="btn-icon btn-round">
                      <i className="tim-icons icon-settings-gear-63" />
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {/* Zona 2: Barra de filtros (Búsqueda por DNI) */}
                <Row className="align-items-end mb-4">
                  <Col md="11">
                    <FormGroup className="mb-0">
                      <Label for="dni">DNI del Alumno</Label>
                      <Input
                        type="text"
                        id="dni"
                        placeholder="Ingresa 8 dígitos"
                        value={dni}
                        maxLength="8"
                        onChange={(e) => {
                          setDni(e.target.value);
                          setAlumno(null);
                          setMensaje("");
                        }}
                        onKeyPress={(e) => e.key === "Enter" && buscarAlumno()}
                        style={{ height: "40px" }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-0">
                      <Button
                        color="info"
                        block
                        onClick={buscarAlumno}
                        disabled={loadingBuscar}
                        style={{ height: "40px", padding: "0" }}
                        title="Buscar"
                      >
                        {loadingBuscar ? (
                          <Spinner size="sm" />
                        ) : (
                          <i className="tim-icons icon-zoom-split" />
                        )}
                      </Button>
                    </FormGroup>
                  </Col>
                </Row>

                {mensaje && <p className="text-warning text-center">{mensaje}</p>}

                {/* Zona 3: Barra de acciones (Espacio reservado o acciones secundarias) */}
                <Row className="mb-4">
                  <Col md="12" className="text-right">
                    {/* Acciones adicionales si las hubiera */}
                  </Col>
                </Row>

                <hr className="my-4" />

                {/* Datos del alumno y controles de generación */}
                {alumno && (
                  <div className="alumno-details fade-in">
                    <Row className="mb-4">
                      <Col md="6">
                        <FormGroup>
                          <Label>Apellidos</Label>
                          <Input
                            type="text"
                            value={`${alumno.apellidopaterno} ${alumno.apellidomaterno}`}
                            disabled
                            style={{ height: "40px" }}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>Nombres</Label>
                          <Input
                            type="text"
                            value={alumno.nombres}
                            disabled
                            style={{ height: "40px" }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col md="6">
                        <FormGroup>
                          <Label>Ciclo Académico</Label>
                          <FSelect
                            value={cicloSeleccionado}
                            onChange={(e) => setCicloSeleccionado(e.target.value)}
                            options={listCiclos.map((c) => ({ value: c.tipociclo || c._id, label: c.tipociclo || c.denominacion }))}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>Salón / Ubicación</Label>
                          <FSelect
                            value={salonSeleccionado}
                            onChange={(e) => setSalonSeleccionado(e.target.value)}
                            options={listSalones.map((s) => ({ value: s.denominacion || s._id, label: s.denominacion }))}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="justify-content-center">
                      <Col md="4">
                        <Button
                          color="success"
                          block
                          size="lg"
                          onClick={generarCarnet}
                          disabled={loadingCarnet}
                          className="btn-fill"
                        >
                          {loadingCarnet ? (
                            <><Spinner size="sm" className="mr-2" /> Generando...</>
                          ) : (
                            <><i className="tim-icons icon-credit-card mr-2" /> Generar Carnet</>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                )}
              </CardBody>
            </Card>

          </Col>
        </Row>
      </div>
    </>
  );
}

export default CrearCarnet;
